import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RankingEditor } from "../../components/common/RankingEditor";
import { TournamentEventForm } from "../../components/common/TournamentEventForm";
import { Button } from "../../components/common/Button";
import { ConfirmModal } from "../../components/common/ConfirmModal";
import { CompetitionBundle, loadCompetitionBundle } from "../../services/tournamentCompetition";
import { CompetitionCreatePanel } from "../../components/common/tournament-event/CompetitionCreatePanel";
import { CompetitionStagePanel } from "../../components/common/tournament-event/CompetitionStagePanel";
import { EventCalendarPanel } from "../../components/common/tournament-event/EventCalendarPanel";
import { EventGeneralPanel } from "../../components/common/tournament-event/EventGeneralPanel";
import { EventTabKey, EventTabs } from "../../components/common/tournament-event/EventTabs";
import { ParticipantsManager } from "../../components/common/tournament-event/ParticipantsManager";
import { TournamentEventProvider } from "../../components/common/tournament-event/TournamentEventContext";
import { ApiTournamentEvent, ApiTournamentResult, CompetitionStageWritePayload, TournamentEvent } from "../../types";
import { 
  createCompetitionStage,
  deleteCompetitionStage,
  deleteTournamentEvent,
  exportTournamentEventMatchesExcel,
  exportTournamentEventMatchesPdf,
  generateCompetitionStage,
  generateKnockoutCompetitionStage,
  generateTournamentEventResults,
  getTournamentEvent,
  updateCompetitionGroupParticipants,
  updateTournamentEvent,
  updateCompetitionStage,
} from "../../api";
import { PAGES_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";
import { currentTenant } from "../../tenants";
import { downloadBlob, toSafeFilename } from "../../utils/download";
import { useAsyncAction } from "../../hooks/useAsyncAction";

const EMPTY_BUNDLE: CompetitionBundle = {
  participants: [],
  stages: [],
  groups: [],
  matches: [],
  standings: [],
};

export const TournamentEventPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const [event, setEvent] = useState<TournamentEvent>();
  const [bundle, setBundle] = useState<CompetitionBundle>(EMPTY_BUNDLE);
  const [activeTab, setActiveTab] = useState<EventTabKey>("general");
  const [rankingOpen, setRankingOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Azioni asincrone: ognuna traccia il proprio stato "in corso" (vedi useAsyncAction).
  const createStage = useAsyncAction();
  const stageAction = useAsyncAction<number>(); // update + generate stage (chiave = stageId)
  const knockoutAction = useAsyncAction<number>();
  const groupAction = useAsyncAction<number>();
  const rankingGen = useAsyncAction();
  const exportExcel = useAsyncAction();
  const exportPdf = useAsyncAction();
  const eventAction = useAsyncAction(); // azioni evento/stage senza indicatore di busy dedicato

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const [eventData, competitionData] = await Promise.all([
          getTournamentEvent(eventId),
          loadCompetitionBundle(eventId),
        ]);
        setEvent(eventData);
        setBundle(competitionData);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, eventId]);

  const canManage = !!(user && (user.can_manage_all || user.can_manage_content));
  const hasParticipants = bundle.participants.length > 0;
  const hasScheduledMatches = useMemo(
    () =>
      bundle.matches.some((match) => {
        if (!match.scheduled_at) return false;
        return !Number.isNaN(new Date(match.scheduled_at).getTime());
      }),
    [bundle.matches]
  );
  const stageTabs = useMemo(
    () =>
      bundle.stages.map((stage) => ({
        key: `stage-${stage.id}`,
        label: stage.name,
        short_label: stage.name,
      })),
    [bundle.stages]
  );
  const visibleTabs: { key: EventTabKey; label: string; short_label: string }[] = useMemo(
    () => {
      const tabs: { key: EventTabKey; label: string; short_label: string }[] = [
        { key: "general", label: "Info generali", short_label: "Info" },
      ];
      if (canManage || hasParticipants) {
        tabs.push({ key: "participants", label: "Partecipanti", short_label: "Partecipanti" });
      }
      if (canManage && hasParticipants) {
        tabs.push({ key: "setup", label: "Setup competizione", short_label: "Setup" });
      }
      if (currentTenant.enableCalendar && hasScheduledMatches) {
        tabs.push({ key: "calendar", label: "Calendario", short_label: "Calendario" });
      }
      tabs.push(...stageTabs);
      return tabs;
    },
    [canManage, hasParticipants, hasScheduledMatches, stageTabs]
  );

  const refreshCompetition = async () => {
    if (!id) return;
    const competitionData = await loadCompetitionBundle(eventId);
    setBundle(competitionData);
  };

  const handleExportEventExcel = async () => {
    if (!event || !bundle.matches.length || exportExcel.isPending) return;
    await exportExcel.run(
      async () => {
        const blob = await exportTournamentEventMatchesExcel(event.id);
        downloadBlob(blob, `calendario_match_${toSafeFilename(event.tournament.name)}.xlsx`);
      },
      { errorMessage: "Errore durante l'esportazione Excel dell'evento." }
    );
  };

  const handleExportEventPdf = async () => {
    if (!event || !bundle.matches.length || exportPdf.isPending) return;
    await exportPdf.run(
      async () => {
        const blob = await exportTournamentEventMatchesPdf(event.id);
        downloadBlob(blob, `calendario_match_${toSafeFilename(event.tournament.name)}.pdf`);
      },
      { errorMessage: "Errore durante l'esportazione PDF dell'evento." }
    );
  };

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab("general");
    }
  }, [activeTab, visibleTabs]);

  const activeStageId = useMemo(() => {
    if (!activeTab.startsWith("stage-")) return null;
    const raw = activeTab.replace("stage-", "");
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }, [activeTab]);

  const activeStage = useMemo(
    () => bundle.stages.find((stage) => Number(stage.id) === activeStageId) ?? null,
    [bundle.stages, activeStageId]
  );

  const canGenerateResultsAutomatically = useMemo(() => {
    if (!event) return false;

    // Keep this logic aligned with TournamentEvent.can_generate_results_automatically
    // in backend/attivita_interna/models.py. If one changes, the other must change too.
    const rankedStages = bundle.stages.filter(
      (stage) => (stage.ranking_start_position ?? 0) > 0
    );

    return (
      event.results.length === 0 &&
      rankedStages.some((stage) => stage.ranking_start_position === 1) &&
      rankedStages.every((stage) => stage.status === "played")
    );
  }, [event, bundle.stages]);

  if (isLoading) {
    return <p className="p-4 text-center text-gray-600">Caricamento...</p>;
  }

  if (!event) {
    return <p className="p-4 text-center text-red-500">Torneo non trovato</p>;
  }

  const handleSaveEvent = async (newEvent: Partial<ApiTournamentEvent>) => {
    await eventAction.run(
      async () => {
        const event_res = await updateTournamentEvent(eventId, newEvent);
        setEvent(event_res);
        setModalOpen(false);
      },
      { errorMessage: "Errore nel salvataggio dell'evento." }
    );
  };

  const handleDeleteEvent = async () => {
    await eventAction.run(
      async () => {
        await deleteTournamentEvent(event.id);
        navigate(PAGES_ENDPOINTS.TORNEI);
      },
      { errorMessage: "Errore in eliminazione dell'evento." }
    );
  };

  const handleSaveResults = async (newResults: Partial<ApiTournamentResult>[]) => {
    await eventAction.run(
      async () => {
        const event_res = await updateTournamentEvent(eventId, { results: newResults, state: "concluso" });
        setEvent(event_res);
        setRankingOpen(false);
      },
      { errorMessage: "Errore nel salvataggio della classifica." }
    );
  };

  const handleCreateStage = async (payload: CompetitionStageWritePayload) => {
    await createStage.run(
      async () => {
        const created = await createCompetitionStage(payload);
        await refreshCompetition();
        setActiveTab(`stage-${created.id}`);
      },
      { errorMessage: "Errore nella creazione dello stage." }
    );
  };

  const handleUpdateStage = async (stageId: number, payload: CompetitionStageWritePayload) => {
    await stageAction.run(
      async () => {
        await updateCompetitionStage(stageId, payload);
        await refreshCompetition();
      },
      { key: stageId, errorMessage: "Errore nell'aggiornamento dello stage." }
    );
  };

  const handleGenerateStage = async (stageId: number, participantIds: number[], matchDurationMinutes: number) => {
    await stageAction.run(
      async () => {
        await generateCompetitionStage(stageId, participantIds, matchDurationMinutes);
        await refreshCompetition();
      },
      { key: stageId, errorMessage: "Errore nella generazione dello stage." }
    );
  };

  const handleDeleteStage = async (stageId: number) => {
    await eventAction.run(
      async () => {
        await deleteCompetitionStage(stageId);
        await refreshCompetition();
        setActiveTab("general");
      },
      { errorMessage: "Errore nell'eliminazione dello stage." }
    );
  };

  const handleGenerateRanking = async () => {
    await rankingGen.run(
      async () => {
        const updatedEvent = await generateTournamentEventResults(eventId);
        setEvent(updatedEvent);
        setRankingOpen(false);
      },
      { errorMessage: "Errore nella generazione automatica della classifica." }
    );
  };

  const handleGenerateKnockoutStage = async (stageId: number) => {
    await knockoutAction.run(
      async () => {
        const result = await generateKnockoutCompetitionStage(stageId);
        await refreshCompetition();
        setActiveTab(`stage-${result.stage.id}`);
      },
      { key: stageId, errorMessage: "Errore nella generazione della fase eliminatoria." }
    );
  };

  const handleUpdateStageGroupParticipants = async (groupId: number, participantIds: number[]) => {
    await groupAction.run(
      async () => {
        await updateCompetitionGroupParticipants(groupId, participantIds);
        await refreshCompetition();
      },
      { key: groupId, errorMessage: "Errore nell'aggiornamento dei partecipanti del girone." }
    );
  };

  const tournamentEventValue = {
    canManage,
    enableCalendar: currentTenant.enableCalendar,
    tournamentName: event.tournament.name,
    onResultSaved: refreshCompetition,
  };

  return (
    <TournamentEventProvider value={tournamentEventValue}>
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{event.tournament.name}</h1>
          <h1 className="text-xl text-gray-600 ml-6 mt-2">{event.season.name}</h1>
        </div>

        {visibleTabs.length > 1 && (
          <EventTabs activeTab={activeTab} onChange={setActiveTab} tabs={visibleTabs} />
        )}

        {activeTab === "general" && (
          <EventGeneralPanel
            event={event}
            canManage={canManage}
            canGenerateResultsAutomatically={canGenerateResultsAutomatically}
            generatingRanking={rankingGen.isPending}
            hasMatches={bundle.matches.length > 0}
            exportingExcel={exportExcel.isPending}
            exportingPdf={exportPdf.isPending}
            onEditInfo={() => setModalOpen(true)}
            onEditRanking={() => setRankingOpen(true)}
            onGenerateRanking={handleGenerateRanking}
            onExportExcel={handleExportEventExcel}
            onExportPdf={handleExportEventPdf}
          />
        )}

        {activeTab === "participants" && (canManage || hasParticipants) && (
          <ParticipantsManager
            participants={bundle.participants}
            eventId={event.id}
            seasonId={event.season.id}
            canManage={canManage}
            onChanged={refreshCompetition}
          />
        )}

        {canManage && activeTab === "setup" && (
          <CompetitionCreatePanel
            canManage={canManage}
            eventId={event.id}
            participantsCount={bundle.participants.length}
            existingStages={bundle.stages}
            creating={createStage.isPending}
            onCreateStage={handleCreateStage}
          />
        )}

        {activeTab === "calendar" && currentTenant.enableCalendar && hasScheduledMatches && (
          <EventCalendarPanel
            matches={bundle.matches}
            stages={bundle.stages}
            participants={bundle.participants}
          />
        )}

        {activeStage && (
          <CompetitionStagePanel
            stage={activeStage}
            stages={bundle.stages}
            groups={bundle.groups}
            matches={bundle.matches}
            standings={bundle.standings}
            participants={bundle.participants}
            generating={stageAction.activeKey === Number(activeStage.id)}
            generatingKnockout={knockoutAction.activeKey === Number(activeStage.id)}
            updatingGroupId={groupAction.activeKey}
            onUpdateStage={(payload) => handleUpdateStage(Number(activeStage.id), payload)}
            onGenerate={(participantIds, matchDurationMinutes) =>
              handleGenerateStage(Number(activeStage.id), participantIds, matchDurationMinutes)
            }
            onGenerateKnockout={() => handleGenerateKnockoutStage(Number(activeStage.id))}
            onUpdateGroupParticipants={handleUpdateStageGroupParticipants}
            onDelete={() => handleDeleteStage(Number(activeStage.id))}
          />
        )}

        {rankingOpen && (
          <RankingEditor
            event={event}
            initialResults={event.results}
            onCancel={() => setRankingOpen(false)}
            onSave={async (newResults) => {
              await handleSaveResults(newResults);
            }}
          />
        )}

        {canManage && activeTab === "general" ? (
          <div className="mt-6 flex items-center justify-between">
            <Button variant="secondary" onClick={() => navigate(PAGES_ENDPOINTS.TORNEI)}>
              Indietro
            </Button>
            <ConfirmModal
              message={`Sei sicuro di voler eliminare questo elemento?`}
              onConfirm={() => handleDeleteEvent()}
              confirmText="Elimina"
              cancelText="Annulla"
            />
          </div>
        ) : null}

        {modalOpen && (
          <TournamentEventForm
            event={event}
            onCancel={() => setModalOpen(false)}
            onSave={async (newEvent: Partial<ApiTournamentEvent>) => {
              await handleSaveEvent(newEvent);
            }}
          />
        )}
      </div>
    </TournamentEventProvider>
  );
};
